import {
  AlertColor,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import DrawerMenu from 'components/DrawerMenu';
import { FC, useEffect, useState } from 'react';
import strings from '../../../../../common/Translation/Translate';
import axiosInstance from '../../../../../utils/axios';
import { URLS } from '../../../../../utils/constants/urls';
import { getDrawerWidth } from 'utils/validators/HelperFunctions';

interface CloneFormState {
  newOrgName: string;
  locationId: string;
  cloneAddOns: boolean;
  clonePricePackages: boolean;
  cloneVenues: boolean;
  cloneVenueConfigs: boolean;
}

interface LocationOption {
  id: number;
  name: string;
}

const emptyForm = (): CloneFormState => ({
  newOrgName: '',
  locationId: '',
  cloneAddOns: false,
  clonePricePackages: false,
  cloneVenues: false,
  cloneVenueConfigs: false,
});

interface CloneOrganizationDrawerProps {
  open: boolean;
  organizationId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const CloneOrganizationDrawer: FC<CloneOrganizationDrawerProps> = ({
  open,
  organizationId,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const drawerWidth = getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg);

  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [sourceOrgName, setSourceOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ newOrgName?: string; locationId?: string }>({});
  const [notification, setNotification] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });
  const [form, setForm] = useState<CloneFormState>(emptyForm());

  useEffect(() => {
    if (!open || !organizationId) return;

    setForm(emptyForm());
    setErrors({});
    setSourceOrgName('');

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [locationRes, orgRes] = await Promise.all([
          axiosInstance.get(URLS.organizationLocation),
          axiosInstance.get(URLS.getOrgGeneralById(organizationId)),
        ]);
        if (locationRes.status === 200) {
          setLocations(locationRes.data);
        }
        if (orgRes.status === 200) {
          const names = orgRes.data?.localizedAttributes;
          const firstName = names ? (Object.values(names)[0] as any) : null;
          setSourceOrgName(orgRes.data?.organizationName || firstName?.name || '');
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };

    void fetchInitialData();
  }, [open, organizationId]);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!form.newOrgName.trim()) newErrors.newOrgName = strings.requiredText;
    if (!form.locationId) newErrors.locationId = strings.requiredText;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof CloneFormState, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!organizationId || !validate()) return;
    try {
      setSaving(true);
      const payload = {
        newOrgName: form.newOrgName.trim(),
        locationId: Number(form.locationId),
        cloneAddOns: form.cloneAddOns,
        clonePricePackages: form.clonePricePackages,
        cloneVenues: form.cloneVenues,
        cloneVenueConfigs: form.cloneVenueConfigs,
      };
      const { status } = await axiosInstance.post(URLS.cloneOrganization(organizationId), payload);
      if (status === 200) {
        onSaved();
      }
    } catch (error: any) {
      setNotification({
        display: true,
        severity: 'error',
        message: error?.response?.data?.message || strings.something_went_wrong || 'Something went wrong',
      });
    } finally {
      setSaving(false);
    }
  };

  const title = sourceOrgName ? `${strings.clone_org_title} — ${sourceOrgName}` : strings.clone_org_title;

  return (
    <>
      {notification.display && (
        <Notification
          isOpen={notification.display}
          message={notification.message}
          severity={notification.severity as AlertColor}
          closeNotification={() => setNotification(prev => ({ ...prev, display: false }))}
        />
      )}
      <DrawerMenu
        open={open}
        handleClose={onClose}
        closeButton
        flexColumn
        noPadding
        overlapHeader
        title={title}
        width={drawerWidth}>
        {(loading || saving) && <PageLoader />}
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 1 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                size="small"
                label={strings.clone_org_name_label}
                value={form.newOrgName}
                onChange={e => handleChange('newOrgName', e.target.value)}
                error={Boolean(errors.newOrgName)}
                helperText={errors.newOrgName}
                required
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                select
                fullWidth
                size="small"
                label={strings.location_title_list || 'Location'}
                value={form.locationId}
                onChange={e => handleChange('locationId', e.target.value)}
                error={Boolean(errors.locationId)}
                helperText={errors.locationId}
                required>
                {locations.map(loc => (
                  <MenuItem key={loc.id} value={String(loc.id)}>
                    {loc.name}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  {strings.clone_org_data_label}
                </Typography>
                <Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.cloneAddOns}
                        onChange={e => handleChange('cloneAddOns', e.target.checked)}
                      />
                    }
                    label={strings.clone_org_addons}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.clonePricePackages}
                        onChange={e => handleChange('clonePricePackages', e.target.checked)}
                      />
                    }
                    label={strings.clone_org_price_packages}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.cloneVenues}
                        onChange={e => {
                          handleChange('cloneVenues', e.target.checked);
                          if (!e.target.checked) {
                            handleChange('cloneVenueConfigs', false);
                          }
                        }}
                      />
                    }
                    label={strings.clone_org_venues}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.cloneVenueConfigs}
                        disabled={!form.cloneVenues}
                        onChange={e => handleChange('cloneVenueConfigs', e.target.checked)}
                      />
                    }
                    label={strings.clone_org_venue_configs}
                  />
                </Stack>
              </Box>
            </Stack>
          </Box>
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 1.5,
              pb: 2,
              mt: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
            }}>
            <Button color="inherit" onClick={onClose} disabled={saving}>
              {strings.cancelText || 'Cancel'}
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving || loading}>
              {strings.clone_org_submit}
            </Button>
          </Box>
        </Box>
      </DrawerMenu>
    </>
  );
};

export default CloneOrganizationDrawer;
